import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useNavigate, useSearchParams } from "react-router-dom";

import BlogPostCard from "../components/BlogPostCard";
import PageIntro from "../components/PageIntro";
import { API_BASE } from "../lib/api";

const PAGE_SIZE = 6;
const yellowFieldSx = {
  "& .MuiInputLabel-root": { color: "#b47d00" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#a56f00" },
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    "& fieldset": { borderColor: "#f7b900" },
    "&:hover fieldset": { borderColor: "#dfa700" },
    "&.Mui-focused fieldset": { borderColor: "#d99f00", borderWidth: 2 },
  },
  "& .MuiSelect-icon": {
    border: "none",
    outline: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    color: "text.secondary",
    right: 8,
    transition: "transform 160ms ease, color 160ms ease",
  },
  "& .MuiSelect-iconOpen": {
    color: "#a56f00",
    transform: "rotate(180deg)",
  },
};

const yellowButtonSx = {
  "&&": {
    bgcolor: "#f7b900",
    backgroundImage: "none",
    color: "#312600",
    borderColor: "#f7b900",
    boxShadow: "none",
    fontWeight: 600,
    textTransform: "none",
  },
  "&&:hover, &&:focus-visible": {
    bgcolor: "#dca600",
    backgroundImage: "none",
    color: "#312600",
    borderColor: "#dca600",
    boxShadow: "none",
  },
};

const tagAutocompletePaperProps = {
  sx: {
    bgcolor: "#fff",
    border: "1px solid #e8e8e8",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    "& .MuiAutocomplete-listbox": { bgcolor: "#fff" },
    "& .MuiAutocomplete-option": {
      backgroundColor: "#fff !important",
      "&:hover, &.Mui-focused": {
        backgroundColor: "#fffaf0 !important",
      },
      '&[aria-selected="true"]': {
        backgroundColor: "#fff7d6 !important",
      },
      '&[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
        backgroundColor: "#ffefb0 !important",
      },
    },
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#fff",
      border: "1px solid #e8e8e8",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      "& .MuiMenuItem-root": {
        backgroundColor: "#fff !important",
        "&:hover, &.Mui-focusVisible": {
          backgroundColor: "#fffaf0 !important",
        },
        "&.Mui-selected": {
          backgroundColor: "#fff7d6 !important",
        },
        "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: "#ffefb0 !important",
        },
      },
    },
  },
};

export default function Blog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState(null);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [sortOrder, setSortOrder] = React.useState("newest");
  const [page, setPage] = React.useState(1);

  const [posts, setPosts] = React.useState([]);
  const [totalPostCount, setTotalPostCount] = React.useState(0);
  const [categories, setCategories] = React.useState([]);
  const [allTags, setAllTags] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/blog/categories/?used=true`).then((response) =>
        response.json(),
      ),
      fetch(`${API_BASE}/blog/tags/?used=true`).then((response) =>
        response.json(),
      ),
    ])
      .then(([categoryData, tagData]) => {
        setCategories(categoryData);
        setAllTags(tagData);
      })
      .catch(() => setError("Could not load blog filters."));
  }, []);

  React.useEffect(() => {
    if (!categoryParam || categories.length === 0) return;

    const normalizedCategoryParam = categoryParam.toLowerCase();
    const matchedCategory = categories.find(
      (item) =>
        String(item.id) === categoryParam ||
        item.name.toLowerCase() === normalizedCategoryParam,
    );

    if (matchedCategory) {
      setCategory((current) =>
        current?.id === matchedCategory.id ? current : matchedCategory,
      );
    }
  }, [categories, categoryParam]);

  React.useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(PAGE_SIZE),
      ordering: sortOrder === "oldest" ? "published_at" : "-published_at",
    });

    if (query.trim()) params.append("search", query.trim());
    if (category?.id) params.append("category", category.id);
    selectedTags.forEach((tag) => params.append("tags", tag.id));

    setIsLoading(true);
    setError(null);
    fetch(`${API_BASE}/blog/posts/?${params.toString()}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load articles.");
        return response.json();
      })
      .then((data) => {
        setPosts(data.results || []);
        setTotalPostCount(data.count || 0);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setError(requestError.message);
        }
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [page, query, category, selectedTags, sortOrder]);

  React.useEffect(() => {
    setPage(1);
  }, [query, category, selectedTags, sortOrder]);

  const pageCount = Math.max(1, Math.ceil(totalPostCount / PAGE_SIZE));

  return (
    <Box sx={{ bgcolor: "#fcfcfc", minHeight: "100%" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <PageIntro title="INNOVakis Blog">
            Latest research insights, innovations, and best practices in
            sustainable agriculture
          </PageIntro>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => navigate("/blog/new")}
            sx={{
              minHeight: 40,
              px: 2.25,
              ...yellowButtonSx,
            }}
          >
            New post
          </Button>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.75 },
            mb: 3.5,
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
              alignItems: "end",
            }}
          >
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search articles..."
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                ...yellowFieldSx,
                "& .MuiInputBase-root": { minHeight: 43 },
              }}
            />

            <FormControl size="small" sx={yellowFieldSx}>
              <InputLabel id="blog-category-label">Category</InputLabel>
              <Select
                labelId="blog-category-label"
                label="Category"
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
                value={category?.id || "all"}
                onChange={(event) => {
                  const id = event.target.value;
                  setCategory(
                    id === "all"
                      ? null
                      : categories.find((item) => item.id === id) || null,
                  );
                }}
                sx={{ minHeight: 43 }}
              >
                <MenuItem value="all">All</MenuItem>
                {categories.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              disableCloseOnSelect
              popupIcon={<KeyboardArrowDownRoundedIcon />}
              componentsProps={{ paper: tagAutocompletePaperProps }}
              options={allTags}
              value={selectedTags}
              onChange={(_, value) => setSelectedTags(value)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value) => (
                <Typography sx={{ fontSize: 13, ml: 0.5 }}>
                  {value.length} selected
                </Typography>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  size="small"
                  placeholder={selectedTags.length ? "" : "Select tags"}
                />
              )}
              sx={{
                ...yellowFieldSx,
                "& .MuiInputLabel-root": { color: "text.secondary" },
                "& .MuiInputBase-root": { minHeight: "43px !important" },

                "& .MuiAutocomplete-popupIndicator": {
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  color: "text.secondary",
                  transition: "transform 160ms ease, color 160ms ease",
                },
                "& .MuiAutocomplete-popupIndicator:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiAutocomplete-popupIndicator.Mui-focusVisible": {
                  outline: "none",
                  boxShadow: "none",
                },
                "& .MuiAutocomplete-popupIndicatorOpen": {
                  color: "#a56f00",
                  transform: "rotate(180deg)",
                },
              }}
            />

            <FormControl size="small" sx={yellowFieldSx}>
              <InputLabel id="blog-sort-label">Sort</InputLabel>
              <Select
                labelId="blog-sort-label"
                label="Sort"
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                sx={{ minHeight: 43 }}
              >
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 10 }}>
            <CircularProgress />
          </Stack>
        ) : posts.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 2.5,
            }}
          >
            {posts.map((post) => (
              <BlogPostCard key={post.id} post={post} />
            ))}
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{ p: 7, textAlign: "center", borderStyle: "dashed" }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No results
            </Typography>
            <Typography color="text.secondary">
              Try changing the search or filters.
            </Typography>
          </Paper>
        )}

        {posts.length > 0 && (
          <Stack alignItems="center" sx={{ mt: 5 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                "& .MuiPagination-ul": { gap: 0.75 },
                "& .MuiPaginationItem-root": {
                  border: "1px solid #efd36c",
                  color: "#9a7000",
                  bgcolor: "#fff",
                  minWidth: 38,
                  height: 38,
                  borderRadius: 1.25,
                  fontSize: 13,
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  color: "#312600",
                  bgcolor: "#f7b900",
                  backgroundImage: "none",
                  borderColor: "#f7b900",
                  "&:hover": { bgcolor: "#dca600" },
                },
              }}
            />
          </Stack>
        )}
      </Container>
    </Box>
  );
}
